import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, Spinner } from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { useCategories, useIdentities } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopShopCategories } from "./desktop/shop-categories.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  店铺分类
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **跟平台的全站品类是两回事，别混。**
 *    平台品类是全站统一的商品学名（瓷砖 / 地板 / 灯具…），上架时选，
 *    改一下影响所有人 —— 那是平台的事。
 *    店铺分类是**这家店自己的货架**：「主推」「工装常用」「样板间同款」，
 *    只出现在他自己的店里，别人看不到，也不该由他去改全站的品类树。
 */

export interface ShopCategory {
  id: number;
  name: string;
  sortOrder: number;
  /**
   * 绑定的平台品类。
   * 🔴 有值 = **自动货架**：店里属于这个品类的商品自动都在里面，
   *    上游新上的货也会自己出现，不用再归一次类。
   */
  platformCategoryId: number | null;
  /** 这个分类下有几件商品 —— 删之前要让他知道会影响多少。 */
  productCount: number;
}

function ShopCategories() {
  const search = useSearch({ from: "/shop-categories" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();
  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;

  const list = useQuery({
    queryKey: ["merchant", orgId, "shop-categories"],
    queryFn: () => orgFetch<ShopCategory[]>("/shop/categories", orgId!),
    enabled: Boolean(orgId),
  });

  const { data: platformCats } = useCategories();

  /**
   * 🔴 **后台改完货架，前台那份缓存也要作废。**
   *    两边是两个查询：后台是 `["merchant", orgId, "shop-categories"]`，
   *    店铺页是 `["shop", shopId, "categories"]`。只作废后台那份的话，
   *    他刚调好顺序、点进自己的店一看**还是老样子** —— 会以为没保存上，
   *    然后反复去调。
   *
   * ⚠️ 商品也要一起作废：自动货架是按平台品类收商品的，
   *    新建/删除一个货架会改变店铺页当前筛选的结果。
   */
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["merchant", orgId, "shop-categories"] });
    void qc.invalidateQueries({ queryKey: ["shop"] });
    void qc.invalidateQueries({ queryKey: ["products"] });
  };

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  /** 正在改名的那一条。⚠️ 一次只让改一个 —— 同时开几个输入框容易存错。 */
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  /** ⚠️ 删除要二次确认，而且要行内确认不弹窗 —— 手机上弹窗盖住内容。 */
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const create = useMutation({
    mutationFn: (v: { name: string; platformCategoryId?: number }) =>
      orgFetch("/shop/categories", orgId!, {
        method: "POST",
        body: JSON.stringify({
          name: v.name.trim(),
          sortOrder: list.data?.length ?? 0,
          platformCategoryId: v.platformCategoryId ?? null,
        }),
      }),
    onSuccess: () => {
      setNewName("");
      setAdding(false);
      void invalidate();
    },
  });

  const rename = useMutation({
    mutationFn: (v: { id: number; name: string }) =>
      orgFetch(`/shop/categories/${v.id}`, orgId!, {
        method: "PATCH",
        body: JSON.stringify({ name: v.name.trim() }),
      }),
    onSuccess: () => {
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      orgFetch(`/shop/categories/${id}`, orgId!, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmDel(null);
      void invalidate();
    },
  });

  /**
   * 🔴 **一键按平台品类建货架。**
   *    新店的分类页不该是一张白纸 —— 平台已经有瓷砖/地板/灯具这套现成的分法，
   *    让每个商家从零手建一遍毫无道理。
   */
  const seed = useMutation({
    mutationFn: () =>
      orgFetch<{ created: number }>("/shop/categories/from-platform", orgId!, { method: "POST" }),
    onSuccess: () => invalidate(),
  });

  /**
   * 调顺序。
   *
   * 🔴 **本地先动，再发请求。**
   *    等服务端回来再刷新的话有两个毛病：
   *    ① 点了之后那一行要过一会儿才动，看着像没点上；
   *    ② **连点两次会算错** —— 第二次用的是还没刷新的旧顺序，
   *       第二步的位移会基于错误的起点，结果跟他想要的不一样。
   */
  const move = useMutation({
    mutationFn: (ids: number[]) =>
      orgFetch("/shop/categories/reorder", orgId!, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onMutate: async (ids) => {
      const key = ["merchant", orgId, "shop-categories"];
      /** ⚠️ 先把在飞的请求取消掉，否则它回来会把本地这次改动覆盖回去。 */
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ShopCategory[]>(key);
      if (prev) {
        const byId = new Map(prev.map((c) => [c.id, c]));
        qc.setQueryData(
          key,
          ids.map((id) => byId.get(id)).filter(Boolean) as ShopCategory[],
        );
      }
      return { prev, key };
    },
    /** ⚠️ 失败要把顺序退回去 —— 否则他看到的顺序跟库里的不一样，还不知道。 */
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: () => invalidate(),
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。**
   *    下面那个查询在 `orgId` 为空时是 disabled 的，而 disabled 的查询
   *    `isPending` 恒为 true —— 未登录、或登录了但名下没有店的人，
   *    看到的是一个**永远停不下来的圈**，页面上没有任何提示也没有出路。
   */
  if (!isPending && !orgId) {
    return (
      <SubPage title="店铺分类">
        <Empty title="你还不是商家" hint="店铺分类是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} />
      </SubPage>
    );
  }
  if (isPending || list.isPending) return <SubPage title="店铺分类"><Spinner /></SubPage>;
  if (list.error) return <SubPage title="店铺分类"><ErrorBlock error={list.error} /></SubPage>;

  const items = list.data ?? [];

  /**
   * ⚠️ 手机上**不做拖拽排序**：小屏幕加长列表，拖拽误触率很高，
   *    而且拖到屏幕外要自动滚动，做不好比不做还糟。
   *    上移/下移两个按钮足够，而且点了立刻能看出结果。
   */
  const swap = (i: number, j: number) => {
    if (j < 0 || j >= items.length) return;
    const ids = items.map((c) => c.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    move.mutate(ids);
  };

  return (
    <SubPage
      title="店铺分类"
      footer={
        adding ? undefined : (
          <Button className="w-full" onClick={() => setAdding(true)}>
            <Plus className="size-4" />
            新增分类
          </Button>
        )
      }
    >
      <div className="space-y-2.5 px-3 py-3">
        <p className="px-1 text-[12px] leading-relaxed text-muted-foreground">
          这是<strong>你自己店里的货架</strong>，客户进你的店时按这个分组看商品。
          跟平台的品类（瓷砖 / 地板 / 灯具）是两回事，只影响你的店。
        </p>

        {adding && (
          <Card className="space-y-2.5 px-4 py-4">
            {/*
              ⚠️ 先给平台品类的快捷选项 —— 大多数人想要的就是这几个，
                 点一下就建好，不用自己打字。
            */}
            {(platformCats ?? []).length > 0 && (
              <div>
                <span className="mb-1.5 block text-[13px] text-muted-foreground">
                  用平台品类（店里属于这个品类的货自动进来）
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {(platformCats ?? [])
                    .filter((c) => !items.some((x) => x.name === c.name))
                    .map((c) => (
                      <button
                        key={c.id}
                        className="rounded-full border border-border px-3 py-1.5 text-[13px] active:bg-secondary"
                        disabled={create.isPending}
                        onClick={() => create.mutate({ name: c.name, platformCategoryId: c.id })}
                      >
                        + {c.name}
                      </button>
                    ))}
                </div>
              </div>
            )}

            <Input
              label="或者自己起个名"
              placeholder="例如：主推 / 工装常用 / 样板间同款"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            {create.error && <ErrorBlock error={create.error} />}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setAdding(false);
                  setNewName("");
                }}
              >
                取消
              </Button>
              <Button
                className="flex-1"
                loading={create.isPending}
                disabled={!newName.trim()}
                onClick={() => create.mutate({ name: newName })}
              >
                保存
              </Button>
            </div>
          </Card>
        )}

        {/*
          ⚠️ **空态这里不用 `Empty`。** 它会撑满整屏高度把下面的按钮顶出视口 ——
             真机上量到按钮在 y=901 而视口只有 900，差一点点，全看不见。
             空态下面还有内容时，就不能用那个"占满剩余高度"的版式。
        */}
        {items.length === 0 && !adding ? (
          <div className="space-y-3 py-10 text-center">
            <p className="text-[15px] font-medium">还没有分类</p>
            <p className="mx-auto max-w-[18rem] text-[13px] leading-relaxed text-muted-foreground">
              可以直接用平台的品类，也可以自己起名，比如「主推」「工装常用」
            </p>
            {/*
              🔴 **给一条"一键搞定"的路。**
                 平台已经有瓷砖/地板/灯具这套现成的分法，
                 让他从零一个个手建一遍毫无道理。
            */}
            <Button
              className="w-full"
              loading={seed.isPending}
              onClick={() => seed.mutate()}
            >
              按平台品类一键生成（{(platformCats ?? []).map((c) => c.name).slice(0, 3).join(" / ")}…）
            </Button>
            {seed.error && <ErrorBlock error={seed.error} />}
          </div>
        ) : (
          items.map((c, i) => (
            <Card key={c.id} className="px-4 py-3">
              {editing?.id === c.id ? (
                <div className="space-y-2.5">
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ id: c.id, name: e.target.value })}
                  />
                  {rename.error && <ErrorBlock error={rename.error} />}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditing(null)}>
                      取消
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1"
                      loading={rename.isPending}
                      disabled={!editing.name.trim()}
                      onClick={() => rename.mutate(editing)}
                    >
                      保存
                    </Button>
                  </div>
                </div>
              ) : confirmDel === c.id ? (
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 text-[13px] text-destructive">
                    {/*
                      🔴 说清楚"商品不会跟着没" —— 不说的话他不敢删，
                         或者删了之后以为把货弄丢了。
                    */}
                    删掉「{c.name}」？分类下的 {c.productCount} 件商品不会消失，只是回到未分类。
                  </span>
                  <Button size="sm" variant="outline" onClick={() => setConfirmDel(null)}>
                    取消
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(c.id)}
                  >
                    删除
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <GripVertical className="size-4 shrink-0 text-muted-foreground/40" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px]">{c.name}</p>
                    <p className="text-[12px] text-muted-foreground">
                      {c.productCount} 件商品
                      {/*
                        ⚠️ 标出来是"自动"还是"手动"：
                           自动货架不用他去归类，不标的话他会白忙一场
                           （去「我的商品」里一件件放，其实根本不需要）。
                      */}
                      {c.platformCategoryId ? " · 按平台品类自动收录" : " · 手动挑选"}
                    </p>
                  </div>
                  <button
                    className="px-1.5 text-[13px] text-muted-foreground disabled:opacity-30"
                    disabled={i === 0 || move.isPending}
                    onClick={() => swap(i, i - 1)}
                  >
                    上移
                  </button>
                  <button
                    className="px-1.5 text-[13px] text-muted-foreground disabled:opacity-30"
                    disabled={i === items.length - 1 || move.isPending}
                    onClick={() => swap(i, i + 1)}
                  >
                    下移
                  </button>
                  <button
                    className="px-1.5 text-muted-foreground"
                    onClick={() => setEditing({ id: c.id, name: c.name })}
                    aria-label="改名"
                  >
                    <Pencil className="size-[15px]" />
                  </button>
                  <button
                    className="px-1.5 text-muted-foreground"
                    onClick={() => setConfirmDel(c.id)}
                    aria-label="删除"
                  >
                    <Trash2 className="size-[15px]" />
                  </button>
                </div>
              )}
            </Card>
          ))
        )}

        {move.error && <ErrorBlock error={move.error} />}
      </div>
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function ShopCategoriesRoute() {
  return useIsDesktop() ? <DesktopShopCategories /> : <ShopCategories />;
}

export const shopCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shop-categories",
  component: ShopCategoriesRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
